import { requiredFieldsOf } from '@atlas/core-utils/work-order'
import { FileType, type RequiredWorkOrderField } from '@atlas/types'
import { useRef, useState } from 'react'
import type { StepFooterCta } from '@/components/work-orders/WorkOrderStepFooter'
import type { WorkOrder } from '@/domain/work-order.types'
import { HookCode, type ResolvedWorkflowSnapshot, type WorkflowTransitionSnapshot } from '@/domain/workflow.types'
import { usePackFiles } from '@/hooks/usePackFiles'
import { useTransitionWorkOrder, useUpdateWorkOrder } from '@/hooks/useWorkOrders'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { DETAILS_STATUS } from '../stage-turn'
import {
	formFromOrder,
	mergeEdits,
	missingFromForm,
	type PackForm,
	type PackFormEdits,
	patchFromForm,
	pruneEdits,
	sharepointUrlError,
	withEdits,
} from './pack-form'

/** Where Send Task takes the order: the designer's turn. */
export const SEND_TASK_TARGET = 'ready_to_design'

/** The fields a move's `fields_present` guards list. */
const requiredFieldsOfMove = (move: WorkflowTransitionSnapshot | null): RequiredWorkOrderField[] => [
	...new Set(
		(move?.guards ?? [])
			.filter((guard) => guard.hookCode === HookCode.FIELDS_PRESENT)
			.flatMap((guard) => requiredFieldsOf(guard.params ?? {})),
	),
]

/** How a save ended: nothing to send, sent, or refused (already reported). */
enum SaveOutcome {
	CLEAN = 'clean',
	SAVED = 'saved',
	FAILED = 'failed',
}

/** How Save Draft ended, told to the PM; a refusal was already reported by the save. */
function reportDraft(outcome: SaveOutcome) {
	if (outcome === SaveOutcome.SAVED) notification.success('Draft saved')
	if (outcome === SaveOutcome.CLEAN) notification.info('No changes to save.')
}

/** A refused Send Task, saying whether the details the PM changed were saved first. */
function reportRefusedMove(error: unknown, outcome: SaveOutcome) {
	const reason = apiErrorMessage(error, 'the move was refused.')
	notification.error(
		outcome === SaveOutcome.SAVED
			? `Details saved, but the task was not sent: ${reason}`
			: `The task was not sent: ${reason}`,
	)
}

/**
 * The form: the order as it stands, with the PM's edits on top, so a field the PM has not
 * touched follows the order (a refresh from Workfront, a save landing). The edits are
 * dropped whenever the order changes status.
 */
function usePackForm(wo: WorkOrder) {
	const [state, setState] = useState<{ status: string; edits: PackFormEdits }>({ status: wo.status, edits: {} })
	if (state.status !== wo.status) setState({ status: wo.status, edits: {} })
	const edits = state.status === wo.status ? state.edits : {}
	const form = withEdits(formFromOrder(wo), edits)
	// Functional, so an edit landing from an async callback never overwrites newer ones.
	const update = (patch: PackFormEdits) => setState((prev) => ({ ...prev, edits: mergeEdits(prev.edits, patch) }))
	const forgetSaved = (saved: PackForm) => setState((prev) => ({ ...prev, edits: pruneEdits(prev.edits, saved) }))
	return { form, update, forgetSaved }
}

/**
 * Save Draft and Send Task over the form. Send Task saves first and moves only once the save
 * has landed, so the guard reads what the PM sees; a press while either is in flight is
 * ignored, so one press is one save and one move.
 */
function useDraftActions(
	wo: WorkOrder,
	form: PackForm,
	sharepointError: string | null,
	forgetSaved: (saved: PackForm) => void,
) {
	const updateOrder = useUpdateWorkOrder(wo.workOrderId)
	const transition = useTransitionWorkOrder()
	const inFlight = useRef(false)
	const [isSending, setIsSending] = useState(false)

	const save = async (): Promise<SaveOutcome> => {
		if (sharepointError) {
			notification.error(sharepointError)
			return SaveOutcome.FAILED
		}
		const patch = patchFromForm(form, wo)
		if (Object.keys(patch).length === 0) return SaveOutcome.CLEAN
		try {
			await updateOrder.mutateAsync({ id: wo.workOrderId, patch })
			forgetSaved(form)
			return SaveOutcome.SAVED
		} catch (error) {
			notification.error(apiErrorMessage(error, `Could not save ${wo.orderCode}.`))
			return SaveOutcome.FAILED
		}
	}

	const saveDraft = async () => reportDraft(await save())

	const move = async (target: WorkflowTransitionSnapshot, outcome: SaveOutcome) => {
		try {
			await transition.mutateAsync({ id: wo.workOrderId, toStatus: target.toStatusId })
			notification.success('Task sent to designer')
		} catch (error) {
			reportRefusedMove(error, outcome)
		}
	}

	const sendTask = async (target: WorkflowTransitionSnapshot) => {
		if (inFlight.current) return
		inFlight.current = true
		setIsSending(true)
		try {
			const outcome = await save()
			if (outcome !== SaveOutcome.FAILED) await move(target, outcome)
		} finally {
			inFlight.current = false
			setIsSending(false)
		}
	}

	return { saveDraft, sendTask, isSending, isSaving: updateOrder.isPending }
}

/**
 * The PM's form on Sources verified, kept until the order leaves the status: what is missing
 * for Send Task, and the footer's Save Draft and Send Task, offered only to whoever may edit.
 */
export function useCompleteDetails(
	wo: WorkOrder,
	snapshot: ResolvedWorkflowSnapshot | null | undefined,
	canEdit: boolean,
) {
	const { form, update, forgetSaved } = usePackForm(wo)
	const current = snapshot?.currentStatusId === wo.status ? snapshot : null
	const target = current?.legalTransitions.find((t) => t.toStatusId === SEND_TASK_TARGET) ?? null
	const required = requiredFieldsOfMove(target)
	const mediaPlans = usePackFiles(
		{ orderCode: wo.orderCode, campaignCode: form.campaignCode },
		FileType.MEDIA_PLAN,
		wo.status === DETAILS_STATUS && form.isCampaign && !!form.campaignCode,
	)
	const missing = missingFromForm(form, wo, required, (mediaPlans.data?.length ?? 0) > 0)
	const sharepointError = sharepointUrlError(form.sharepointFolderUrl)
	const { saveDraft, sendTask, isSending, isSaving } = useDraftActions(wo, form, sharepointError, forgetSaved)

	const secondary: StepFooterCta | null = canEdit
		? { label: 'Save Draft', onPress: () => void saveDraft(), isDisabled: isSending, isPending: isSaving && !isSending }
		: null
	const cta: StepFooterCta | null =
		canEdit && target
			? {
					label: target.ctaLabel,
					onPress: () => void sendTask(target),
					// A draft still saving would race the move: the guard could read the order before it.
					isDisabled: missing.length > 0 || sharepointError !== null || isSaving,
					isPending: isSending,
				}
			: null

	return { form, update, required, missing, sharepointError, secondary, cta }
}
